import { Button, TextArea } from '@consigli/facade';
import { useSubmitFeedbackMutation } from '@consigli/hooks';
import { FeedbackType } from '@consigli/types';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SlPaperPlane } from 'react-icons/sl';
import { toast } from 'react-toastify';

import crashIcon from '@/assets/images/crash-icon.svg';
import { redact } from '@/monitoring/redact';

export interface ErrorPageProps {
  /** The thrown value. Any shape — it arrives from a boundary or the router. */
  error: unknown;
  /**
   * Whether to offer the feedback form. Submitting needs a bearer token —
   * `prepareHeaders` throws `'No token'` without one — so callers pass a
   * session-presence check rather than inviting a write-up that cannot send.
   */
  canSendFeedback?: boolean;
}

/**
 * Redacts, because this is a second exit for the error and `normalise.ts` only
 * covers the monitoring one. Both values are rendered into the page and POSTed
 * to the feedback table the backoffice displays, and `react-guard.tsx` throws
 * `JSON.stringify(sess)` — so without this the user is shown their own JWT.
 */
const readMessage = (error: unknown): { message: string; stack: string } => {
  if (error instanceof Error) {
    return { message: redact(error.message), stack: redact(error.stack ?? '') };
  }
  if (typeof error === 'object' && error !== null && 'message' in error) {
    return { message: redact(String((error as { message: unknown }).message)), stack: '' };
  }
  return { message: '', stack: '' };
};

/**
 * The crash screen, for both the router's `errorElement` and the provider-stack
 * boundary. Takes the error as a prop rather than reading `useRouteError()`,
 * which works only in the former, so there is one crash UI instead of two.
 */
export const ErrorPage = ({ error, canSendFeedback = true }: ErrorPageProps) => {
  // The boundary renders this outside `<Suspense>`, where a fallback that
  // suspends is a blank page.
  const { t } = useTranslation(undefined, { useSuspense: false });
  const [submitFeedback, { isLoading }] = useSubmitFeedbackMutation();
  const [description, setDescription] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const { message: errorMessage, stack: errorStack } = readMessage(error);

  const handleSubmit = async () => {
    const message = [
      description.trim(),
      '',
      `--- ${t('error.message')} ---`,
      errorMessage,
      ...(errorStack ? ['', '--- Stack trace ---', errorStack] : []),
    ].join('\n');

    try {
      await submitFeedback({
        type: FeedbackType.ERROR,
        message,
      }).unwrap();
      setSubmitted(true);
      toast.success(t('error.feedback-success'));
    } catch {
      toast.error(t('error.feedback-error'));
    }
  };

  return (
    <div className="min-h-screen flex flex-col justify-center items-center px-4">
      <img src={crashIcon} alt="crash icon" className="w-1/5 pb-10" />
      <div className="text-title">{t('error.general')}</div>
      {canSendFeedback && <div className="text-primary mt-1">{t('error.send-text')}</div>}
      <div className="text-tertiary text-day-neutral-subtle mt-6">{t('error.message')}</div>
      <div className="mb-2">{errorMessage}</div>
      {canSendFeedback && !submitted && (
        <div className="flex flex-col items-center mt-4 gap-3 w-[500px] max-w-full">
          <div className="flex flex-col w-full">
            <TextArea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('error.description-placeholder')}
              rows={4}
              className="w-full"
            />
          </div>
          <Button
            secondary
            onClick={handleSubmit}
            rounded
            className="text-base font-semibold"
            icon={SlPaperPlane}
            disabled={isLoading || !description.trim()}
          >
            {isLoading ? t('error.submitting') : t('error.send-button')}
          </Button>
        </div>
      )}
      {canSendFeedback && submitted && (
        <div className="text-primary mt-4">{t('error.feedback-success')}</div>
      )}
    </div>
  );
};
